/* eslint-disable max-len */
import { createSelector } from 'reselect';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { DATASET, INTERNAL_UNKNOWN } from '@utils/constants';
import { combineWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import { INVERTED_METRIC_SWITCH_MAP, BASE_METRICS } from '@utils/metrics';
import { SCOPE } from './types';

import {
  allFiltersSelector,
  selectedMetricsSelector,
  selectedDimensionSelector,
  appliedSortSelector,
} from '../base/selectors';

const selector = (state) => get(state, SCOPE);

const metricHistoricalRequestBody = {
  queries: [
    {
      type: 'group-by',
      dataset: DATASET,
      metrics: [],
      filter: {},
      limit: 200,
      groupBy: [],
      queryGranularity: 'ALL',
      interval: '',
      sortBy: [],
      orderBy: 'desc',
      options: {
        withTotals: true,
      },
    },
  ],
};

export const payloadSelector = createSelector(
  selectedMetricsSelector,
  allFiltersSelector,
  selectedDimensionSelector,
  appliedSortSelector,
  (metrics, filters, dimension, appliedSort) =>
    filters.map((eachFilter) => {
      const invertedMetrics = [];
      const metricsList = combineWithMetricDeDuplication(metrics, BASE_METRICS);
      metricsList.forEach((m) => {
        if (m in INVERTED_METRIC_SWITCH_MAP) {
          invertedMetrics.push(INVERTED_METRIC_SWITCH_MAP[m]);
        }
      });
      const body = cloneDeep(metricHistoricalRequestBody);
      body.queries[0].limit = 200;
      body.queries[0].metrics = [...metricsList, ...invertedMetrics];
      body.queries[0].filter = eachFilter.filter.rule;
      body.queries[0].interval = `${moment(eachFilter.interval.from).format()}/${moment(eachFilter.interval.to).format()}`;
      body.queries[0].sortBy = [appliedSort];
      body.queries[0].groupBy = [dimension];
      return body;
    }),
);

export const dataSelector = createSelector(selector, (historicalRangeData) =>
  historicalRangeData.response ? historicalRangeData.response : [],
);

export const loadingSelector = createSelector(selector, (historicalRangeData) => ({
  loading: historicalRangeData.isFetching,
  errorCode: historicalRangeData.errorCode,
  errorMsg: historicalRangeData.errorMsg,
}));

const getUniqueDimensions = (data) => {
  let flattenedDimensionKeys = [];
  // get the complete set of dimensions names and set id
  const dimensionKeys = [];
  data.forEach((eachData) => {
    const newDimensionKeySet = [];
    eachData.forEach((row) => {
      const rowId = row.value.join('');
      // eslint-disable-next-line no-param-reassign
      row.id = rowId;
      newDimensionKeySet.push(rowId);
    });
    dimensionKeys.push(newDimensionKeySet);
  });
  // flatten the dimension name array list and create a set to remove duplicates and convert it back to array
  flattenedDimensionKeys = [...new Set(dimensionKeys.flat())];
  return flattenedDimensionKeys;
};

const setMissingValues = (data, metrics) => {
  // create empty metrics data
  const emptyMetrics = {};
  metrics.forEach((metric) => {
    emptyMetrics[metric] = 0;
  });

  const flattenedDimensionKeys = getUniqueDimensions(data);

  const mergedAllData = data.flat(Infinity);

  // loop through the each data and check whether the demsnion is present if not add empty metric data
  data.map((tableData) => {
    flattenedDimensionKeys.forEach((dimensionKey) => {
      const present = tableData.some((o) => o.id === dimensionKey);
      if (!present) {
        const found = mergedAllData.find((eachRow) => eachRow.id === dimensionKey);
        tableData.push({
          ...emptyMetrics,
          id: dimensionKey,
          value: [dimensionKey],
          name: [found && found.name ? found.name.join('') : INTERNAL_UNKNOWN],
        });
      }
    });
    return tableData;
  });
  return data;
};

export const historicalRangeMaxMetricsDataSelector = createSelector(
  dataSelector,
  selectedMetricsSelector,
  (tableData, metrics) => {
    const maxMetrics = {};
    const invertedMetrics = [];
    metrics.forEach((m) => {
      if (m in INVERTED_METRIC_SWITCH_MAP) {
        invertedMetrics.push(INVERTED_METRIC_SWITCH_MAP[m]);
      }
    });
    const fullMetrics = [...metrics, ...invertedMetrics];
    tableData.forEach((eachData) => {
      get(eachData[0], 'data[0].results', []).forEach((row) => {
        fullMetrics.forEach((metric) => {
          if (maxMetrics[metric]) {
            if (row[metric] > maxMetrics[metric]) {
              maxMetrics[metric] = row[metric];
            }
          } else {
            maxMetrics[metric] = row[metric];
          }
        });
      });
    });
    return maxMetrics;
  },
);

export const historicalRangeTableDataSortSelector: any = createSelector(
  dataSelector,
  selectedMetricsSelector,
  appliedSortSelector,
  (tableData, metrics, sort) => {
    let unknownRow = null;
    const records: any = [];
    const newTableData = cloneDeep(tableData);
    const invertedMetrics = [];
    metrics.forEach((m) => {
      if (m in INVERTED_METRIC_SWITCH_MAP) {
        invertedMetrics.push(INVERTED_METRIC_SWITCH_MAP[m]);
      }
    });
    const cherryPickData = newTableData.map((eachFilterData) =>
      get(eachFilterData[0], 'data[0].results', []),
    );
    const prepareData = setMissingValues(cherryPickData, [...metrics, ...invertedMetrics]);
    if (prepareData[sort.filter]) {
      prepareData[sort.filter].forEach((row) => {
        // Unknown row
        if (row.id !== INTERNAL_UNKNOWN) {
          records.push(row);
        } else {
          unknownRow = row;
        }
      });
      // sort data
      const sortedData = orderBy(records, sort.metric, sort.order);
      if (unknownRow) {
        sortedData.push(unknownRow);
      }
      prepareData[sort.filter] = sortedData;
    }
    return prepareData;
  },
);

export const historicalRangeTotalDataSelector = createSelector(dataSelector, (historicalData) => {
  const totalData: any = [];
  historicalData.forEach((eachData) => {
    const data = get(eachData[0], 'totals[0].results', {});
    totalData.push(data);
  });
  return totalData;
});

export default selector;
